/**
 * Strukturiertes Logging-System für die Stundenliste-Anwendung
 *
 * Features:
 * - Log-Levels (DEBUG, INFO, WARN, ERROR)
 * - Kontext-Informationen (Komponente, Aktion)
 * - Timestamps
 * - Strukturierte Daten
 * - Nur in Entwicklung vollständige Ausgabe
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  data?: unknown;
  error?: Error;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// In Produktion nur WARN und ERROR loggen
const MIN_LOG_LEVEL: LogLevel = import.meta.env.PROD ? 'WARN' : 'DEBUG';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[MIN_LOG_LEVEL];
}

function formatTimestamp(): string {
  return new Date().toISOString();
}

function formatLogEntry(entry: LogEntry): string {
  const { level, message, timestamp, context } = entry;
  const contextStr = context?.component ? `[${context.component}]` : '';
  const actionStr = context?.action ? `(${context.action})` : '';

  return `${timestamp} ${level} ${contextStr}${actionStr} ${message}`;
}

function logToConsole(entry: LogEntry): void {
  const formattedMessage = formatLogEntry(entry);

  switch (entry.level) {
    case 'DEBUG':
      console.debug(formattedMessage, entry.data ?? '');
      break;
    case 'INFO':
      console.info(formattedMessage, entry.data ?? '');
      break;
    case 'WARN':
      console.warn(formattedMessage, entry.data ?? '');
      break;
    case 'ERROR':
      console.error(formattedMessage, entry.error ?? entry.data ?? '');
      if (entry.error?.stack) {
        console.error('Stack:', entry.error.stack);
      }
      break;
  }
}

/**
 * Erstellt einen Logger mit optionalem Kontext
 */
export function createLogger(context?: LogContext) {
  const log = (level: LogLevel, message: string, data?: unknown, error?: Error) => {
    if (!shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: formatTimestamp(),
      context,
      data,
      error,
    };

    logToConsole(entry);
  };

  return {
    debug: (message: string, data?: unknown) => log('DEBUG', message, data),
    info: (message: string, data?: unknown) => log('INFO', message, data),
    warn: (message: string, data?: unknown) => log('WARN', message, data),
    error: (message: string, error?: Error | unknown, data?: unknown) => {
      const err = error instanceof Error ? error : undefined;
      log('ERROR', message, data, err);
    },

    /**
     * Erstellt einen Child-Logger mit erweitertem Kontext
     */
    child: (childContext: LogContext) =>
      createLogger({ ...context, ...childContext }),

    /**
     * Loggt die Dauer einer Operation
     */
    time: (label: string) => {
      const start = performance.now();
      return () => {
        const duration = Math.round(performance.now() - start);
        log('DEBUG', `${label} completed in ${duration}ms`);
      };
    },
  };
}

// Standard-Logger ohne Kontext
export const logger = createLogger();

// Spezialisierte Logger für häufige Anwendungsfälle
export const authLogger = createLogger({ component: 'Auth' });
export const syncLogger = createLogger({ component: 'Sync' });
export const storageLogger = createLogger({ component: 'Storage' });
export const calcLogger = createLogger({ component: 'Calculations' });

/**
 * Middleware zum Loggen von Aktionen
 * Kann mit Context/Reducer verwendet werden
 */
export function logAction(actionType: string, payload?: unknown): void {
  logger.debug(`Action: ${actionType}`, payload);
}

/**
 * Loggt unbehandelte Fehler
 * Wird automatisch von ErrorBoundary verwendet
 */
export function logUnhandledError(error: Error, context?: LogContext): void {
  const errorLogger = createLogger({ component: 'Unhandled', ...context });
  errorLogger.error('Unhandled error occurred', error, {
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Performance-Logging für kritische Operationen
 */
export function measurePerformance<T>(
  label: string,
  operation: () => T,
  context?: LogContext
): T {
  const perfLogger = createLogger({ ...context, action: 'performance' });
  const endTimer = perfLogger.time(label);
  try {
    const result = operation();
    endTimer();
    return result;
  } catch (error) {
    perfLogger.error(`${label} failed`, error as Error);
    throw error;
  }
}

/**
 * Async Performance-Logging
 */
export async function measurePerformanceAsync<T>(
  label: string,
  operation: () => Promise<T>,
  context?: LogContext
): Promise<T> {
  const perfLogger = createLogger({ ...context, action: 'performance' });
  const endTimer = perfLogger.time(label);
  try {
    const result = await operation();
    endTimer();
    return result;
  } catch (error) {
    perfLogger.error(`${label} failed`, error as Error);
    throw error;
  }
}
